import type { LandingContent } from '../../shared/content/landingContentTypes.js';
import { isIndustryPageKey } from '../../shared/content/industryPages.js';
import type { StaticPageContent, StaticPageKey } from '../../shared/content/staticPageContent.js';
import type { LandingEditorElementDefinition } from './landingEditorRegistry.js';
import { mobileNavigationEditorDefinitions } from './mobileNavigationEditorDefinitions.js';

export const industryNavigationEditorDefinitions = (content: LandingContent): LandingEditorElementDefinition[] => {
  const definitions: LandingEditorElementDefinition[] = [];
  content.industryNav?.forEach((_, index) => {
    const id = `navigation.industry.${index}`;
    definitions.push(
      {
        id,
        label: `Branchenmenü ${index + 1}`,
        section: 'hero',
        kind: 'container',
        parentId: 'navigation.container',
        repeatPath: 'industryNav',
        repeatIndex: index,
      },
      {
        id: `${id}.label`,
        label: `Branchenmenü ${index + 1}: Titel`,
        section: 'hero',
        path: `industryNav.${index}.label`,
        parentId: id,
        kind: 'text',
        typographyMode: 'visual',
      },
      {
        id: `${id}.text`,
        label: `Branchenmenü ${index + 1}: Beschreibung`,
        section: 'hero',
        path: `industryNav.${index}.text`,
        parentId: id,
        kind: 'text',
        multiline: true,
        typographyMode: 'semantic',
      },
      {
        id: `${id}.href`,
        label: `Branchenmenü ${index + 1}: Link`,
        section: 'hero',
        path: `industryNav.${index}.href`,
        parentId: id,
        kind: 'url',
      },
    );
  });
  return definitions;
};

export const industryGalleryEditorDefinitions = (
  pageKey: StaticPageKey,
  page: StaticPageContent,
  heroId: string,
): LandingEditorElementDefinition[] => {
  const definitions: LandingEditorElementDefinition[] = [];
  const industry = isIndustryPageKey(pageKey);
  if (industry) {
    definitions.push({
      id: `${heroId}.gallery`,
      label: 'Hero: Bilderkarten',
      parentId: `${heroId}.layout`,
      kind: 'container',
    });
    definitions.push({
      id: `${heroId}.backgroundWord`,
      label: 'Hero: Branche im Hintergrund',
      parentId: heroId,
      path: 'heroBackgroundWord',
      kind: 'text',
      typographyMode: 'visual',
    });
  }
  page.heroGallery?.forEach((photo, index) => {
    const id = `${heroId}.portrait.${index}`;
    definitions.push(
      {
        id,
        label: `Hero: Bilderkarte ${index + 1}`,
        parentId: industry ? `${heroId}.gallery` : heroId,
        kind: 'container',
        repeatPath: 'heroGallery',
        repeatIndex: index,
      },
      {
        id: `${id}.image`,
        label: `Hero: Kachel ${index + 1}`,
        path: `heroGallery.${index}.image`,
        parentId: id,
        kind: 'image',
      },
      {
        id: `${id}.imageAlt`,
        label: `Hero: Kachel ${index + 1} Alternativtext`,
        path: `heroGallery.${index}.imageAlt`,
        parentId: id,
        kind: 'text',
        multiline: true,
        typographyMode: 'none',
      },
    );
    if (photo.title !== undefined)
      definitions.push({
        id: `${id}.title`,
        label: `Hero: Kachel ${index + 1} Titel`,
        path: `heroGallery.${index}.title`,
        parentId: id,
        kind: 'text',
        typographyMode: 'visual',
      });
  });
  return definitions.map(definition => ({ ...definition, source: 'static', staticPageKey: pageKey }));
};

export const industryStaticNavigationEditorDefinitions = (
  content: LandingContent,
): LandingEditorElementDefinition[] => [
  { id: 'navigation.container', label: 'Navigation', kind: 'container', parentId: 'page.root' },
  ...industryNavigationEditorDefinitions(content).map(definition => ({ ...definition, section: undefined })),
  ...mobileNavigationEditorDefinitions(content).map(definition => ({ ...definition, section: undefined })),
];
